/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Optional } from './@types/common';
import type { QueryDict } from './http';
import type { XHubChatEvent } from './models/event/event';
import unhomoglyph from 'unhomoglyph';
import { M_TIMESTAMP } from './@types/location';
import { ConnectionError } from './http';
import { logger } from './utils/logger';

/**
 * String representing the lowercase latin alphabet for use in {@link secureRandomStringFrom}
 * (can be combined with other such exports or other characters by appending strings)
 */
export const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';

/**
 * String representing the uppercase latin alphabet for use in secureRandomStringFrom
 * (can be combined with other such exports or other characters by appending strings)
 */
export const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * String representing the arabic numerals for use in {@link secureRandomStringFrom}
 * (can be combined with other such exports or other characters by appending strings)
 */
export const DIGITS = '0123456789';

/**
 * Removes the direction override characters from a string
 * @returns string with chars removed
 */
export function removeDirectionOverrideChars(str: string): string {
  if (typeof str === 'string') {
    return str.replace(/[\u202D\u202E]/g, '');
  }
  return '';
}

/**
 * Checks that the given object has the specified keys.
 * @param obj - The object to check.
 * @param keys - The list of keys that 'obj' must have.
 * @throws If the object is missing keys.
 */
// note using 'keys' here would shadow the 'keys' function defined above
export function checkObjectHasKeys(obj: object, keys: string[]): void {
  for (const key of keys) {
    // eslint-disable-next-line no-prototype-builtins
    if (!obj.hasOwnProperty(key)) {
      throw new Error(`Missing required key: ${key}`);
    }
  }
}

/**
 * Deep copy the given object. The object MUST NOT have circular references and
 * MUST NOT have functions.
 * @param obj - The object to deep copy.
 * @returns A copy of the object without any references to the original.
 */
export function deepCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Encode a dictionary of query parameters.
 * Omits any undefined/null values.
 * @param params - A dict of key/values to encode e.g.
 * `{"foo": "bar", "baz": "taz"}`
 * @returns The encoded string e.g. foo=bar&baz=taz
 */
export function encodeParams(params: QueryDict, urlSearchParams?: URLSearchParams): URLSearchParams {
  const searchParams = urlSearchParams ?? new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== null) {
      if (Array.isArray(val)) {
        val.forEach((v) => {
          searchParams.append(key, String(v));
        });
      } else {
        searchParams.append(key, String(val));
      }
    }
  }
  return searchParams;
}

/**
 * The removeElement() method removes the first element in the array that
 * satisfies (returns true) the provided testing function.
 * @param array - The array.
 * @param fn - Function to execute on each value in the array, with the
 * function signature `fn(element, index, array)`. Return true to
 * remove this element and break.
 * @param reverse - True to search in reverse order.
 * @returns True if an element was removed.
 */
export function removeElement<T>(array: T[], fn: (t: T, i?: number, a?: T[]) => boolean, reverse?: boolean): boolean {
  let i: number;
  if (reverse) {
    for (i = array.length - 1; i >= 0; i--) {
      if (fn(array[i], i, array)) {
        array.splice(i, 1);
        return true;
      }
    }
  } else {
    for (i = 0; i < array.length; i++) {
      if (fn(array[i], i, array)) {
        array.splice(i, 1);
        return true;
      }
    }
  }
  return false;
}

/**
 * Returns a promise which resolves with a given value after the given number of ms
 */
export function sleep<T>(ms: number, value?: T): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms, value);
  });
}

const interns = new Map<string, string>();
/**
 * Internalizes a string, reusing a known pointer or storing the pointer
 * if needed for future strings. (Sử dụng lại một con trỏ đã biết hoặc lưu trữ con trỏ,
 *  giúp giảm bộ nhớ bằng tái sử dụng các đối tượng chuỗi giống hệt nhau thông qua 1 con trỏ duy nhất.)
 * @param str - The string to internalize.
 * @returns The internalized string.
 */
export function internalizeString(str: string): string {
  // Unwrap strings before entering the map, if we somehow got a wrapped
  // string as our input. This should only happen from tests.
  if (typeof (str as unknown) === 'string') {
    str = str.toString();
  }

  // Check the map to see if we can store the value
  if (!interns.has(str)) {
    interns.set(str, str);
  }

  // Return any cached string reference
  return interns.get(str)!;
}

export function ensureNoTrailingSlash(url: string): string;
export function ensureNoTrailingSlash(url: undefined): undefined;
export function ensureNoTrailingSlash(url?: string): string | undefined;
export function ensureNoTrailingSlash(url?: string): string | undefined {
  if (url?.endsWith('/')) {
    return url.slice(0, -1);
  } else {
    return url;
  }
}

/**
 * Generate a cryptographically secure random string using characters given.
 *
 * @param len - The length of the string to generate (must be positive and less than 32768).
 * @param chars - The characters to use in the random string (between 2 and 256 characters long).
 * @returns Random string of characters of length `len`.
 */
export function secureRandomStringFrom(len: number, chars: string): string {
  // This is intended for latin strings so 256 possibilities should be more than enough and
  // means we can use random bytes, minimizing the amount of entropy we need to ask for.
  if (chars.length < 2 || chars.length > 256) {
    throw new Error('Character set must be between 2 and 256 characters long');
  }

  if (len < 1 || len > 32768) {
    throw new Error('Requested random string length must be between 1 and 32768');
  }

  // We'll generate random unsigned bytes, so get the largest number less than 256 that is a multiple
  // of the length of the character set: We'll need to discard any random values that are larger than
  // this as we can't possibly map them onto the character set while keeping each character equally
  // likely to be chosen (minus 1 to convert to indices in a string). (Essentially, we're using a d8
  // to choose between 7 possibilities and re-rolling on an 8, keeping all 7 outcomes equally likely.)
  // Our random values must be strictly less than this
  const randomValueCutoff = 256 - (256 % chars.length);

  // Grab 30% more entropy than we need. This should be enough that we can discard the values that are
  // too high without having to go back and grab more unless we're super unlucky.
  const entropyBuffer = new Uint8Array(Math.floor(len * 1.3));
  // Mark all of this buffer as used to start with (we haven't populated it with entropy yet) so it will
  // be filled on the first iteration.
  let entropyBufferPos = entropyBuffer.length;

  const result = [];
  while (result.length < len) {
    if (entropyBufferPos === entropyBuffer.length) {
      globalThis.crypto.getRandomValues(entropyBuffer);
      entropyBufferPos = 0;
    }

    const randomByte = entropyBuffer[entropyBufferPos++];

    if (randomByte < randomValueCutoff) {
      result.push(chars[randomByte % chars.length]);
    }
  }

  return result.join('');
}

export function secureRandomBase64Url(len: number): string {
  const key = new Uint8Array(len);
  globalThis.crypto.getRandomValues(key);

  return encodeUnpaddedBase64Url(key);
}

/**
 * Generates a random string of uppercase and lowercase letters plus digits using a
 * cryptographically secure random number generator.
 * @param len The length of the string to generate
 * @returns Random string of uppercase and lowercase letters plus digits of length `len`
 */
export function secureRandomString(len: number): string {
  return secureRandomStringFrom(len, UPPERCASE + LOWERCASE + DIGITS);
}

/**
 * Base64 encoding and decoding utilities
 */

function toBase64(uint8Array: Uint8Array, options: Uint8ArrayToBase64Options): string {
  if (typeof uint8Array.toBase64 === 'function') {
    // Currently this is only supported in Firefox,
    // but we match the options in the hope in the future we can rely on it for all environments.
    // https://tc39.es/proposal-arraybuffer-base64/spec/#sec-uint8array.prototype.tobase64
    return uint8Array.toBase64(options);
  }

  let base64 = btoa(uint8Array.reduce((acc, current) => acc + String.fromCharCode(current), ''));
  if (options.omitPadding) {
    base64 = base64.replace(/={1,2}$/, '');
  }
  if (options.alphabet === 'base64url') {
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_');
  }

  return base64;
}

/**
 * Encode a typed array of uint8 as base64.
 * @param uint8Array - The data to encode.
 * @returns The base64.
 */
export function encodeBase64(uint8Array: Uint8Array): string {
  return toBase64(uint8Array, { alphabet: 'base64', omitPadding: false });
}

/**
 * Encode a typed array of uint8 as unpadded base64.
 * @param uint8Array - The data to encode.
 * @returns The unpadded base64.
 */
export function encodeUnpaddedBase64(uint8Array: Uint8Array): string {
  return toBase64(uint8Array, { alphabet: 'base64', omitPadding: true });
}

/**
 * Encode a typed array of uint8 as unpadded base64 using the URL-safe encoding.
 * @param uint8Array - The data to encode.
 * @returns The unpadded base64.
 */
export function encodeUnpaddedBase64Url(uint8Array: Uint8Array): string {
  return toBase64(uint8Array, { alphabet: 'base64url', omitPadding: true });
}

// Regex matching bunch of unicode control characters and otherwise misleading/invisible characters.
// Includes:
// various width spaces U+2000 - U+200D
// LTR and RTL marks U+200E and U+200F
// LTR/RTL and other directional formatting marks U+202A - U+202F
// Arabic Letter RTL mark U+061C
// Combining characters U+0300 - U+036F
// Zero width no-break space (BOM) U+FEFF
// Blank/invisible characters (U2800, U2062-U2063)
// eslint-disable-next-line regexp/no-dupe-characters-character-class, no-misleading-character-class
const removeHiddenCharsRegex = /[\u2000-\u200F\u202A-\u202F\u0300-\u036F\u061C\u2800\u2062\u2063\s]/g;

/**
 * Removes zero width chars, diacritics and whitespace from the string
 * Also applies an unhomoglyph on the string, to prevent similar looking chars
 * @param str - the string to remove hidden characters from
 * @returns a string with the hidden characters removed
 */
export function removeHiddenChars(str: string): string {
  if (typeof str === 'string') {
    return unhomoglyph(str.normalize('NFD').replace(removeHiddenCharsRegex, ''));
  }
  return '';
}

/**
 * A Map implementation that automatically creates default values for missing keys.
 *
 * This class extends the built-in Map class and provides functionality to automatically
 * generate default values when accessing keys that don't exist in the map.
 *
 * @template K - The type of keys in the map
 * @template V - The type of values in the map
 *
 * @example
 * ```typescript
 * // Create a map that defaults to empty arrays
 * const map = new MapWithDefault<string, number[]>(() => []);
 *
 * // This will create and return a new empty array
 * const arr = map.getOrCreate('newKey');
 * arr.push(1, 2, 3);
 *
 * // Subsequent calls return the same array
 * const sameArr = map.getOrCreate('newKey'); // Returns [1, 2, 3]
 * ```
 */
export class MapWithDefault<K, V> extends Map<K, V> {
  /**
   * Creates a new MapWithDefault instance.
   *
   * @param createDefault - A factory function that creates default values for missing keys.
   *                       This function is called each time a new default value is needed.
   */
  public constructor(private createDefault: () => V) {
    super();
  }

  /**
   * Gets the value for the specified key, creating a default value if the key doesn't exist.
   *
   * If the key already exists in the map, returns the existing value.
   * If the key doesn't exist, creates a new value using the createDefault function,
   * stores it in the map under the specified key, and returns it.
   *
   * @param key - The key to look up or create a value for
   * @returns The existing value for the key, or a newly created default value
   *
   * @example
   * ```typescript
   * const counters = new MapWithDefault<string, number>(() => 0);
   *
   * // First call creates and returns 0
   * let count = counters.getOrCreate('user1'); // 0
   *
   * // Increment the counter
   * counters.set('user1', count + 1);
   *
   * // Subsequent calls return the stored value
   * count = counters.getOrCreate('user1'); // 1
   * ```
   */
  public getOrCreate(key: K): V {
    if (!this.has(key)) {
      this.set(key, this.createDefault());
    }

    return this.get(key)!;
  }
}

function getContentTimestampWithFallback(event: XHubChatEvent): number {
  return M_TIMESTAMP.findIn<number>(event.getContent()) ?? -1;
}
/**
 * Sort events by their content m.ts property
 * Latest timestamp first
 */
export function sortEventsByLatestContentTimestamp(left: XHubChatEvent, right: XHubChatEvent): number {
  return getContentTimestampWithFallback(right) - getContentTimestampWithFallback(left);
}

/**
 * Encodes a URI according to a set of template variables. Variables will be
 * passed through encodeURIComponent.
 * @param pathTemplate - The path with template variables e.g. '/foo/$bar'.
 * @param variables - The key/value pairs to replace the template
 * variables with. E.g. `{ "$bar": "baz" }`.
 * @returns The result of replacing all template variables e.g. '/foo/baz'.
 */
export function encodeUri(pathTemplate: string, variables: Record<string, Optional<string>>): string {
  for (const key in variables) {
    // eslint-disable-next-line no-prototype-builtins
    if (!variables.hasOwnProperty(key)) {
      continue;
    }
    const value = variables[key];
    if (value === undefined || value === null) {
      continue;
    }
    pathTemplate = pathTemplate.replace(key, encodeURIComponent(value));
  }
  return pathTemplate;
}

export function promiseTry<T>(fn: () => T | Promise<T>): Promise<T> {
  return Promise.resolve(fn());
}

/**
 * Retries a network operation run in a callback.
 * @param maxAttempts - maximum attempts to try
 * @param callback - callback that returns a promise of the network operation. If rejected with ConnectionError, it will be retried by calling the callback again.
 * @returns the result of the network operation
 * @throws {@link ConnectionError} If after maxAttempts the callback still throws ConnectionError
 */
export async function retryNetworkOperation<T>(maxAttempts: number, callback: () => Promise<T>): Promise<T> {
  let attempts = 0;
  let lastConnectionError: ConnectionError | null = null;
  while (attempts < maxAttempts) {
    try {
      if (attempts > 0) {
        const timeout = 1000 * 2 ** attempts;
        logger.log(`network operation failed ${attempts} times, retrying in ${timeout}ms...`);
        await sleep(timeout);
      }
      return await callback();
    } catch (err) {
      if (err instanceof ConnectionError) {
        attempts += 1;
        lastConnectionError = err;
      } else {
        throw err;
      }
    }
  }
  throw lastConnectionError;
}

/**
 * Compare two objects for equality. The objects MUST NOT have circular references.
 *
 * @param x - The first object to compare.
 * @param y - The second object to compare.
 *
 * @returns true if the two objects are equal
 */
export function deepCompare(x: any, y: any): boolean {
  // Inspired by
  // http://stackoverflow.com/questions/1068834/object-comparison-in-javascript#1144249

  // Compare primitives and functions.
  // Also check if both arguments link to the same object.
  if (x === y) {
    return true;
  }

  if (typeof x !== typeof y) {
    return false;
  }

  // special-case NaN (since NaN !== NaN)
  if (typeof x === 'number' && Number.isNaN(x) && Number.isNaN(y)) {
    return true;
  }

  // special-case null (since typeof null == 'object', but null.constructor
  // throws)
  if (x === null || y === null) {
    return x === y;
  }

  // everything else is either an unequal primitive, or an object
  if (!(x instanceof Object)) {
    return false;
  }

  // check they are the same type of object
  if (x.constructor !== y.constructor || x.prototype !== y.prototype) {
    return false;
  }

  // special-casing for some special types of object
  if (x instanceof RegExp || x instanceof Date) {
    return x.toString() === y.toString();
  }

  // the object algorithm works for Array, but it's sub-optimal.
  if (Array.isArray(x)) {
    if (x.length !== y.length) {
      return false;
    }

    for (let i = 0; i < x.length; i++) {
      if (!deepCompare(x[i], y[i])) {
        return false;
      }
    }
  } else {
    // check that all of y's direct keys are in x
    for (const p in y) {
      if (Object.prototype.hasOwnProperty.call(y, p) !== Object.prototype.hasOwnProperty.call(x, p)) {
        return false;
      }
    }

    // finally, compare each of x's keys with y
    for (const p in x) {
      if (Object.prototype.hasOwnProperty.call(y, p) !== Object.prototype.hasOwnProperty.call(x, p) || !deepCompare(x[p], y[p])) {
        return false;
      }
    }
  }
  return true;
}
